import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import React from "react";
import { setupService } from "../../../../Core/Api/services";
import { DbPlatformInfos } from "../../../../Core/Api/Types/db-platform-infos";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import SettingsGlobalSectionAspera from "./SettingsGlobalSections/settings-global-section-aspera";
import SettingsGlobalSectionExternalLinks from "./SettingsGlobalSections/settings-global-section-external-links";
import SettingsGlobalSectionGlobal from "./SettingsGlobalSections/settings-global-section-global";
import SettingsGlobalSectionMediaShares from "./SettingsGlobalSections/settings-global-section-media-shares";
import SettingsGlobalSectionOwner from "./SettingsGlobalSections/settings-global-section-owner";
import SettingsGlobalSectionPlanning from "./SettingsGlobalSections/settings-global-section-planning";
import SettingsGlobalSectionWowza from "./SettingsGlobalSections/settings-global-section-wowza";
import SettingsGlobalSectionMisc from "./SettingsGlobalSections/settings-global-section-misc";
import SettingsGlobalSectionBilling from "./SettingsGlobalSections/settings-global-section-billing";
import { useTranslation } from "react-i18next";
import SettingsGlobalSectionRessourceImages from "./SettingsGlobalSections/settings-global-ressource-images";
import { UserHasAcl } from "../../../../Utils/helpers";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";

export interface SectionGlobalProps {
  platformInfos: DbPlatformInfos;
  setPlatformInfos: React.Dispatch<React.SetStateAction<DbPlatformInfos>>;
}

function SettingsGlobal() {
  const { t } = useTranslation();
  const [platformInfos, setPlatformInfos] = React.useState<DbPlatformInfos>(new DbPlatformInfos());
  const enqueueCustomSnackbar = useCustomSnackbar();

  React.useEffect(() => {
    setupService.GetPlatformInfos().then(function (response) {
      if (response.success && response.platform)
        setPlatformInfos({ ...response.platform, licences: response.licences });
    });
  }, []);

  const onClickConfirm = () => {
    setupService.UpdatePlatformInfos(platformInfos).then(function (response) {
      if (response.success) enqueueCustomSnackbar(t("alert:success.platformUpdated"), "success");
      else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "error");
    });
  };

  const onClickReload = () => {
    setupService.ReloadConfig().then((response) => {
      if (response.success) enqueueCustomSnackbar(t("alert:success.signalSentSuccessfully"), "success");
      else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "error");
    });
  };

  return (
    <Stack sx={{ flex: "1 1 0%", overflowY: "scroll", py: 2 }}>
      <Typography variant="h3" sx={{ mb: 3 }}>
        {t("page:body.manageYourPlatformSettings")}
      </Typography>
      <SettingsGlobalSectionGlobal key={"global"} platformInfos={platformInfos} setPlatformInfos={setPlatformInfos} />
      <SettingsGlobalSectionOwner key={"owner"} platformInfos={platformInfos} setPlatformInfos={setPlatformInfos} />
      <SettingsGlobalSectionWowza key={"wowza"} platformInfos={platformInfos} setPlatformInfos={setPlatformInfos} />
      <SettingsGlobalSectionAspera key={"aspera"} platformInfos={platformInfos} setPlatformInfos={setPlatformInfos} />
      <SettingsGlobalSectionExternalLinks
        key={"external-inks"}
        platformInfos={platformInfos}
        setPlatformInfos={setPlatformInfos}
      />
      <SettingsGlobalSectionMediaShares
        key={"media-share"}
        platformInfos={platformInfos}
        setPlatformInfos={setPlatformInfos}
      />
      <SettingsGlobalSectionPlanning
        key={"planning"}
        platformInfos={platformInfos}
        setPlatformInfos={setPlatformInfos}
      />
      <SettingsGlobalSectionMisc key={"misc"} platformInfos={platformInfos} setPlatformInfos={setPlatformInfos} />
      <SettingsGlobalSectionBilling key={"billing"} platformInfos={platformInfos} setPlatformInfos={setPlatformInfos} />
      {UserHasAcl(AclTypes.aclSuperUser) && (
        <SettingsGlobalSectionRessourceImages
          key={"ressource-images"}
          platformInfos={platformInfos}
          setPlatformInfos={setPlatformInfos}
        />
      )}
      <Stack direction="row" spacing={3}>
        <Button onClick={onClickConfirm} variant="contained">
          {t("button:confirmChanges")}
        </Button>
        <Button onClick={onClickReload}>{t("button:sendSignalToAllApp")}</Button>
      </Stack>
    </Stack>
  );
}

export default SettingsGlobal;
